import fs from 'node:fs';
import path from 'node:path';
import { logger } from '../utils/logger.js';

/**
 * Item selection state for extract/copy decision
 */
interface SelectionItem {
  name: string;
  type: 'agent' | 'prompt' | 'tool' | 'model' | 'hook';
  filePath: string;
  mode: 'extract' | 'copy';
  isShared: boolean;
  sharedWith: string[];
}

/**
 * Interactive select prompt for choosing from a list of options.
 */
async function selectAgent(agents: string[]): Promise<string> {
  return new Promise((resolve) => {
    const stdin = process.stdin;
    const stdout = process.stdout;

    let selectedIndex = 0;

    const renderOptions = () => {
      stdout.write('\x1B[?25l'); // Hide cursor
      stdout.write(`\x1B[${agents.length + 1}A`); // Move up
      stdout.write('\x1B[0J'); // Clear from cursor to end

      stdout.write('Select an agent to pack (use arrows, enter to confirm):\n');
      agents.forEach((agent, i) => {
        const prefix = i === selectedIndex ? '\x1B[36m❯\x1B[0m' : ' ';
        const highlight = i === selectedIndex ? '\x1B[36m' : '\x1B[90m';
        stdout.write(`${prefix} ${highlight}${agent}\x1B[0m\n`);
      });
    };

    // Initial render
    stdout.write('Select an agent to pack (use arrows, enter to confirm):\n');
    agents.forEach((agent, i) => {
      const prefix = i === selectedIndex ? '\x1B[36m❯\x1B[0m' : ' ';
      const highlight = i === selectedIndex ? '\x1B[36m' : '\x1B[90m';
      stdout.write(`${prefix} ${highlight}${agent}\x1B[0m\n`);
    });

    const wasRaw = stdin.isRaw;
    if (stdin.isTTY) {
      stdin.setRawMode(true);
    }
    stdin.resume();
    stdin.setEncoding('utf8');

    const cleanup = () => {
      stdin.setRawMode(wasRaw ?? false);
      stdin.pause();
      stdin.removeListener('data', onData);
      stdout.write('\x1B[?25h'); // Show cursor
    };

    const onData = (key: string) => {
      if (key === '\x1B[A' || key === 'k') { // Up arrow or k
        selectedIndex = (selectedIndex - 1 + agents.length) % agents.length;
        renderOptions();
      } else if (key === '\x1B[B' || key === 'j') { // Down arrow or j
        selectedIndex = (selectedIndex + 1) % agents.length;
        renderOptions();
      } else if (key === '\r' || key === '\n') { // Enter
        cleanup();
        resolve(agents[selectedIndex]);
      } else if (key === '\x03') { // Ctrl+C
        cleanup();
        stdout.write('\n');
        process.exit(1);
      }
    };

    stdin.on('data', onData);
  });
}

/**
 * Interactive item mode selector for extract/copy decisions.
 */
async function selectItemModes(
  items: SelectionItem[],
  _agentName: string
): Promise<SelectionItem[]> {
  return new Promise((resolve) => {
    const stdin = process.stdin;
    const stdout = process.stdout;

    let selectedIndex = 0;

    const getDisplayLines = () => {
      const lines: string[] = [];
      lines.push('');
      lines.push('Remove originals? (space to toggle, enter to confirm)');
      lines.push('');

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        const isSelected = i === selectedIndex;
        const cursor = isSelected ? '\x1B[36m❯\x1B[0m' : ' ';
        const modeText = item.mode === 'extract'
          ? '\x1B[32m(extract)\x1B[0m'
          : '\x1B[90m(copy)\x1B[0m';

        lines.push(`${cursor} ${item.name} \x1B[90m(${item.type})\x1B[0m ${modeText}`);
      }

      lines.push('');
      return lines;
    };

    const render = (initial = false) => {
      const lines = getDisplayLines();
      if (!initial) {
        stdout.write('\x1B[?25l');
        stdout.write(`\x1B[${lines.length}A`);
        stdout.write('\x1B[0J');
      }
      stdout.write(lines.join('\n') + '\n');
    };

    render(true);

    const wasRaw = stdin.isRaw;
    if (stdin.isTTY) {
      stdin.setRawMode(true);
    }
    stdin.resume();
    stdin.setEncoding('utf8');

    const cleanup = () => {
      stdin.setRawMode(wasRaw ?? false);
      stdin.pause();
      stdin.removeListener('data', onData);
      stdout.write('\x1B[?25h');
    };

    const onData = (key: string) => {
      if (key === '\x1B[A' || key === 'k') {
        selectedIndex = (selectedIndex - 1 + items.length) % items.length;
        render();
      } else if (key === '\x1B[B' || key === 'j') {
        selectedIndex = (selectedIndex + 1) % items.length;
        render();
      } else if (key === ' ') {
        items[selectedIndex].mode = items[selectedIndex].mode === 'extract' ? 'copy' : 'extract';
        render();
      } else if (key === '\r' || key === '\n') {
        cleanup();
        resolve(items);
      } else if (key === '\x03') {
        cleanup();
        stdout.write('\n');
        process.exit(1);
      }
    };

    stdin.on('data', onData);
  });
}

interface PackOptions {
  output?: string;
  npm?: boolean;
  version?: string;
  noRemove?: boolean;
  noInteractive?: boolean;
}

/**
 * Check if this is a valid Standard Agents project.
 */
function isStandardAgentsProject(): boolean {
  // Check for agents directory
  if (!fs.existsSync('agents')) {
    return false;
  }

  // Check for @standardagents/builder dependency
  try {
    const pkgJson = JSON.parse(fs.readFileSync('package.json', 'utf-8'));
    const deps = { ...pkgJson.dependencies, ...pkgJson.devDependencies };
    return '@standardagents/builder' in deps;
  } catch {
    return false;
  }
}

/**
 * Pack an agent with all its dependencies.
 */
export async function pack(agentName: string | undefined, options: PackOptions) {
  logger.info('Packing agent...\n');

  // Verify this is a Standard Agents project
  if (!isStandardAgentsProject()) {
    logger.error('This does not appear to be a Standard Agents project.');
    logger.info('Make sure you have @standardagents/builder installed and an agents/ directory.');
    process.exit(1);
  }

  const rootDir = process.cwd();

  // Dynamic import of packing service (uses separate entry point without Cloudflare deps)
  let PackingService: any;
  try {
    const packing = await import('@standardagents/builder/packing');
    PackingService = packing.PackingService;
  } catch (err) {
    logger.error('Failed to load packing service from @standardagents/builder');
    logger.info('Make sure @standardagents/builder is installed and built.');
    if (err instanceof Error) {
      logger.info(`Error: ${err.message}`);
    }
    process.exit(1);
  }

  const packingService = new PackingService();

  // If no agent name provided, show interactive select
  if (!agentName) {
    const agents = packingService.listAgents(rootDir);

    if (agents.length === 0) {
      logger.error('No agents found in agents/agents/');
      process.exit(1);
    }

    if (agents.length === 1) {
      // Only one agent, use it directly
      agentName = agents[0];
      logger.info(`Found one agent: ${agentName}`);
    } else {
      // Multiple agents, show interactive select
      console.log('');
      agentName = await selectAgent(agents);
      console.log('');
    }
  }

  // At this point agentName is guaranteed to be defined (we exit if no agents found)
  if (!agentName) {
    // This should never happen, but satisfies TypeScript
    logger.error('No agent selected');
    process.exit(1);
  }
  const selectedAgent = agentName;

  logger.info(`Analyzing agent: ${selectedAgent}`);

  // Analyze the agent
  const analysis = await packingService.analyzeAgent(selectedAgent, rootDir);

  if (analysis.errors.length > 0) {
    logger.error('Analysis failed:');
    for (const error of analysis.errors) {
      console.log(`  - ${error}`);
    }
    process.exit(1);
  }

  // Show analysis results
  console.log('');
  logger.info('Discovered constituents:');
  console.log(`  Agents:  ${analysis.constituents.agents.map((a: { name: string }) => a.name).join(', ') || '(none)'}`);
  console.log(`  Prompts: ${analysis.constituents.prompts.map((p: { name: string }) => p.name).join(', ') || '(none)'}`);
  console.log(`  Tools:   ${analysis.constituents.tools.map((t: { name: string }) => t.name).join(', ') || '(none)'}`);
  console.log(`  Models:  ${analysis.constituents.models.map((m: { name: string }) => m.name).join(', ') || '(none)'}`);
  console.log(`  Hooks:   ${analysis.constituents.hooks.map((h: { name: string }) => h.name).join(', ') || '(none)'}`);

  // Show warnings
  if (analysis.warnings.length > 0) {
    console.log('');
    logger.warning('Warnings:');
    for (const warning of analysis.warnings) {
      console.log(`  - ${warning}`);
    }
  }

  // Build selection items from analysis
  const selectionItems: SelectionItem[] = [];

  for (const agent of analysis.constituents.agents) {
    selectionItems.push({
      name: agent.name,
      type: 'agent',
      filePath: agent.filePath,
      mode: agent.sharedWith.length > 0 ? 'copy' : 'extract',
      isShared: agent.sharedWith.length > 0,
      sharedWith: agent.sharedWith,
    });
  }

  for (const prompt of analysis.constituents.prompts) {
    selectionItems.push({
      name: prompt.name,
      type: 'prompt',
      filePath: prompt.filePath,
      mode: prompt.sharedWith.length > 0 ? 'copy' : 'extract',
      isShared: prompt.sharedWith.length > 0,
      sharedWith: prompt.sharedWith,
    });
  }

  for (const tool of analysis.constituents.tools) {
    selectionItems.push({
      name: tool.name,
      type: 'tool',
      filePath: tool.filePath,
      mode: tool.sharedWith.length > 0 ? 'copy' : 'extract',
      isShared: tool.sharedWith.length > 0,
      sharedWith: tool.sharedWith,
    });
  }

  for (const model of analysis.constituents.models) {
    selectionItems.push({
      name: model.name,
      type: 'model',
      filePath: model.filePath,
      mode: model.sharedWith.length > 0 ? 'copy' : 'extract',
      isShared: model.sharedWith.length > 0,
      sharedWith: model.sharedWith,
    });
  }

  for (const hook of analysis.constituents.hooks) {
    selectionItems.push({
      name: hook.name,
      type: 'hook',
      filePath: hook.filePath,
      mode: hook.sharedWith.length > 0 ? 'copy' : 'extract',
      isShared: hook.sharedWith.length > 0,
      sharedWith: hook.sharedWith,
    });
  }

  // Show interactive selector unless --no-interactive is specified
  let itemSelections: { name: string; type: string; filePath: string; mode: 'extract' | 'copy' }[] | undefined;

  if (!options.noInteractive && process.stdin.isTTY && selectionItems.length > 0) {
    console.log('');
    const selections = await selectItemModes(selectionItems, selectedAgent);
    itemSelections = selections.map(s => ({
      name: s.name,
      type: s.type,
      filePath: s.filePath,
      mode: s.mode,
    }));
    console.log('');
  }

  // Determine output directory
  const outputDir = options.output || path.join(rootDir, 'agents', 'packed');

  // Execute packing
  logger.info(`Packing to: ${outputDir}`);

  const result = await packingService.pack({
    agentName: selectedAgent,
    rootDir,
    outputDir,
    version: options.version || '1.0.0',
    npm: options.npm || false,
    removeOriginals: itemSelections ? false : !options.noRemove, // Let itemSelections handle removal
    packageId: `standardagent-${selectedAgent.replace(/_/g, '-')}`,
    itemSelections,
  });

  if (!result.success) {
    logger.error(`Packing failed: ${result.error}`);
    process.exit(1);
  }

  // Show result
  console.log('');
  logger.success('Packing complete!');
  console.log(`  Package ID: ${result.packageId}`);
  console.log(`  Output:     ${result.outputPath}`);
  console.log(`  Files:      ${result.filesCreated.length} created`);

  if (result.filesRemoved && result.filesRemoved.length > 0) {
    console.log(`  Removed:    ${result.filesRemoved.length} original files`);
  }

  if (result.warnings.length > 0) {
    console.log('');
    logger.warning('Warnings:');
    for (const warning of result.warnings) {
      console.log(`  - ${warning}`);
    }
  }

  if (options.npm) {
    console.log('');
    logger.info('NPM package structure created. To publish:');
    console.log(`  cd ${result.outputPath}`);
    console.log('  npm publish');
  }
}
